"use client"

import { useSyncExternalStore } from "react"
import { ADD_ON_PRODUCT_IDS, PRODUCT_IDS } from "@/lib/booking"

/*
 * The check-up lives outside any one page (Eugene, 01.10: "keep the set
 * across the whole site"): one store in the browser, kept in localStorage,
 * so the navbar's cart on every page and the pricing builder show the same
 * picks, and another tab follows. "Clear all"'s undo is in it too, so the
 * corner note can offer it whichever cart cleared. The pricing builder's
 * `useCheckUp` edits it; any Book button reads it (`useSavedBookingProducts`).
 */

/** `mri` is an MRI package id from the plan's MRI cards, `advanced` or `pro`. */
export type Picks = { mri: string | null; tests: string[] }
export type CartState = Picks & { cleared: Picks | null }

const CART_KEY = "ahead-check-up-v1"
const EMPTY: CartState = { mri: null, tests: [], cleared: null }
let cart: CartState = EMPTY
let loaded = false
const listeners = new Set<() => void>()

function readCart(): CartState {
  try {
    const raw = window.localStorage.getItem(CART_KEY)
    if (!raw) return EMPTY
    const saved = JSON.parse(raw) as Partial<Picks>
    return {
      mri: typeof saved.mri === "string" ? saved.mri : null,
      tests: Array.isArray(saved.tests)
        ? saved.tests.filter((k): k is string => typeof k === "string")
        : [],
      cleared: null,
    }
  } catch {
    return EMPTY
  }
}

export function setCart(next: CartState) {
  cart = next
  try {
    window.localStorage.setItem(
      CART_KEY,
      JSON.stringify({ mri: next.mri, tests: next.tests })
    )
  } catch {
    // Private mode or storage off: the picks last for this page view.
  }
  listeners.forEach((listener) => listener())
}

export function subscribeCart(listener: () => void) {
  listeners.add(listener)
  const onStorage = (e: StorageEvent) => {
    if (e.key !== CART_KEY) return
    cart = readCart()
    listeners.forEach((l) => l())
  }
  window.addEventListener("storage", onStorage)
  return () => {
    listeners.delete(listener)
    window.removeEventListener("storage", onStorage)
  }
}

export function cartSnapshot() {
  if (!loaded) {
    loaded = true
    cart = readCart()
  }
  return cart
}

export const serverCart = () => EMPTY

const MRI_PRODUCT: Readonly<Record<string, string>> = {
  advanced: PRODUCT_IDS.advanced,
  pro: PRODUCT_IDS.pro,
}

/**
 * The saved check-up as the booking's `products`: the MRI package or the
 * plan's blood test first, then the saved tests the booking sells. Null while
 * nothing is saved, so a Book button keeps its own link. Lets the navbar's
 * "Book check-up" and every page's Book button carry the picks into the
 * booking app, not only the cart's own button (Eugene, 08.10).
 */
export function useSavedBookingProducts(): {
  products: string[]
  mri: boolean
} | null {
  const { mri, tests } = useSyncExternalStore(
    subscribeCart,
    cartSnapshot,
    serverCart
  )
  const pkg = mri ? MRI_PRODUCT[mri] : undefined
  const addOnIds = tests
    .map((key) => ADD_ON_PRODUCT_IDS[key])
    .filter((id): id is string => !!id)
  if (!pkg && addOnIds.length === 0) return null
  return { products: [pkg ?? PRODUCT_IDS.bloodTest, ...addOnIds], mri: !!pkg }
}
